import { ElementType, ForwardRefExoticComponent, ReactNode, RefAttributes, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { Avatar as DefaultAvatar, AvatarProps } from '@atoms/dataDisplay';
import { Button } from '@atoms/buttons';
import { FaIcon } from '@atoms/icons';
import { Box, Stack } from '@mui/material';

type ImageUploadFieldProps = {
  title: ReactNode;
  subtitle?: ReactNode;
  buttonTitle: ReactNode;
  deleteButtonTitle?: ReactNode;
  onChange?: (file: File | null) => void;
  initialImgSrc?: string | null;
  accept?: string;
  error?: string;
  slots?: {
    Avatar?:
      | ElementType<AvatarProps>
      | ForwardRefExoticComponent<Omit<AvatarProps, 'ref'> & RefAttributes<HTMLDivElement>>;
  };
};
export function ImageUploadField({
  title,
  subtitle,
  buttonTitle,
  onChange,
  deleteButtonTitle = <FormattedMessage id="common.deleteImage" />,
  initialImgSrc,
  accept = '.png,.jpg,.jpeg',
  error,
  slots,
}: ImageUploadFieldProps) {
  const { Avatar: LocalAvatar = DefaultAvatar } = slots ?? {};
  const [src, setSrc] = useState(initialImgSrc ?? '');
  const inputRef = useRef<HTMLInputElement>(null);
  const updateFile = (file: File | null) => {
    if (file) {
      const reader = new FileReader();
      reader.onload = function (e) {
        if (e.target?.result) {
          setSrc(e.target.result as string);
        }
      };
      reader.readAsDataURL(file);
    } else {
      setSrc('');
    }
    onChange?.(file);
  };

  return (
    <Stack direction="row" spacing={2}>
      <Box>
        <LocalAvatar sx={{ width: 64, height: 64 }} src={src} />
      </Box>
      <Stack direction="column">
        <Typography variant="subtitle2">{title}</Typography>
        {error ? (
          <Typography variant="caption" color="error">
            {error}
          </Typography>
        ) : (
          subtitle && <Typography variant="caption">{subtitle}</Typography>
        )}

        <Stack direction="row" mt={2} gap={2}>
          <Button
            component="label"
            role={undefined}
            variant="outlined"
            tabIndex={-1}
            size="small"
            startIcon={<FaIcon name="arrow-up-from-bracket" color="primary" />}
          >
            {buttonTitle}
            <VisuallyHiddenInput
              ref={inputRef}
              type="file"
              accept={accept}
              onChange={(event) => {
                const file = event.target.files?.[0] ?? null;
                updateFile(file);
              }}
            />
          </Button>
          <Button
            startIcon={<FaIcon name="trash-can" />}
            variant="passive"
            onClick={() => {
              updateFile(null);
              if (inputRef.current) {
                inputRef.current.value = '';
              }
            }}
          >
            {deleteButtonTitle}
          </Button>
        </Stack>
      </Stack>
    </Stack>
  );
}
const VisuallyHiddenInput = styled('input')({
  clip: 'rect(0 0 0 0)',
  clipPath: 'inset(50%)',
  height: 1,
  overflow: 'hidden',
  position: 'absolute',
  bottom: 0,
  left: 0,
  whiteSpace: 'nowrap',
  width: 1,
});
